import { useState, useCallback, useEffect } from 'react';
import * as faceapi from '@vladmandic/face-api';
import { User, FileImage, CheckCircle, XCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { CameraCapture } from './CameraCapture';

interface FaceMatchResult {
  similarity: number;
  matched: boolean;
  requiresReview: boolean;
  documentEmbedding: number[];
  selfieEmbedding: number[];
}

interface FaceMatchProps {
  documentImage: string;
  onComplete: (result: FaceMatchResult) => void;
  onError?: (error: string) => void;
  className?: string;
}

export function FaceMatch({
  documentImage,
  onComplete,
  onError,
  className = '',
}: FaceMatchProps) {
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [step, setStep] = useState<'capture' | 'processing' | 'result'>('capture');
  const [selfieImage, setSelfieImage] = useState<string | null>(null);
  const [result, setResult] = useState<FaceMatchResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [processingStatus, setProcessingStatus] = useState('');

  // Load face-api models
  useEffect(() => {
    const loadModels = async () => {
      try {
        const MODEL_URL = '/models';
        await Promise.all([
          faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL),
          faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
          faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
        ]);
        setModelsLoaded(true);
      } catch (err) {
        const errorMsg = 'Erro ao carregar modelos de reconhecimento facial';
        setError(errorMsg);
        onError?.(errorMsg);
      }
    };
    loadModels();
  }, [onError]);

  const extractFaceDescriptor = async (imageData: string): Promise<Float32Array | null> => {
    const img = await faceapi.fetchImage(imageData);
    const detection = await faceapi
      .detectSingleFace(img)
      .withFaceLandmarks()
      .withFaceDescriptor();

    if (!detection) {
      return null;
    }

    return detection.descriptor;
  };

  const cosineSimilarity = (a: Float32Array, b: Float32Array): number => {
    let dotProduct = 0;
    let normA = 0;
    let normB = 0;

    for (let i = 0; i < a.length; i++) {
      dotProduct += a[i] * b[i];
      normA += a[i] * a[i];
      normB += b[i] * b[i];
    }

    return dotProduct / (Math.sqrt(normA) * Math.sqrt(normB));
  };

  const processFaceMatch = useCallback(async (selfie: string) => {
    setStep('processing');
    setProcessingStatus('Extraindo face do documento...');

    try {
      // Extract face from document
      const documentDescriptor = await extractFaceDescriptor(documentImage);
      if (!documentDescriptor) {
        throw new Error('Não foi possível detectar face no documento');
      }

      setProcessingStatus('Extraindo face da selfie...');

      // Extract face from selfie
      const selfieDescriptor = await extractFaceDescriptor(selfie);
      if (!selfieDescriptor) {
        throw new Error('Não foi possível detectar face na selfie');
      }

      setProcessingStatus('Comparando faces...');

      // Calculate similarity
      const similarity = cosineSimilarity(documentDescriptor, selfieDescriptor);

      // Determine match result based on thresholds
      const matched = similarity >= 0.85;
      const requiresReview = similarity >= 0.70 && similarity < 0.85;

      const matchResult: FaceMatchResult = {
        similarity,
        matched,
        requiresReview,
        documentEmbedding: Array.from(documentDescriptor),
        selfieEmbedding: Array.from(selfieDescriptor),
      };

      setResult(matchResult);
      setStep('result');
      onComplete(matchResult);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Erro ao processar comparação facial';
      setError(errorMsg);
      onError?.(errorMsg);
    }
  }, [documentImage, onComplete, onError]);

  const handleSelfieCapture = useCallback((imageData: string) => {
    setSelfieImage(imageData);
    processFaceMatch(imageData);
  }, [processFaceMatch]);

  const handleRetry = () => {
    setSelfieImage(null);
    setResult(null);
    setError(null);
    setStep('capture');
  };

  if (!modelsLoaded) {
    return (
      <div className={`flex flex-col items-center justify-center p-8 ${className}`}>
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin mb-4" />
        <p className="text-gray-600">Carregando modelos de reconhecimento...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex flex-col items-center justify-center p-8 bg-red-50 rounded-lg ${className}`}>
        <XCircle className="w-16 h-16 text-red-400 mb-4" />
        <p className="text-red-600 text-center mb-4">{error}</p>
        <button
          onClick={handleRetry}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${className}`}>
      {/* Document preview */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
          <FileImage className="w-4 h-4" />
          Foto do Documento
        </h3>
        <div className="w-32 h-32 rounded-lg overflow-hidden border-2 border-gray-200">
          <img
            src={documentImage}
            alt="Documento"
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      {/* Capture step */}
      {step === 'capture' && (
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
            <User className="w-4 h-4" />
            Tire uma selfie
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Posicione seu rosto de frente para a câmera, em um ambiente bem iluminado.
          </p>
          <CameraCapture
            onCapture={handleSelfieCapture}
            facingMode="user"
            showPreview={false}
          />
        </div>
      )}

      {/* Processing step */}
      {step === 'processing' && (
        <div className="flex flex-col items-center justify-center p-8">
          <div className="flex gap-4 mb-6">
            <div className="w-24 h-24 rounded-lg overflow-hidden border-2 border-gray-200">
              <img src={documentImage} alt="Documento" className="w-full h-full object-cover" />
            </div>
            <div className="flex items-center">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
            <div className="w-24 h-24 rounded-lg overflow-hidden border-2 border-gray-200">
              {selfieImage && (
                <img src={selfieImage} alt="Selfie" className="w-full h-full object-cover" />
              )}
            </div>
          </div>
          <p className="text-gray-600">{processingStatus}</p>
        </div>
      )}

      {/* Result step */}
      {step === 'result' && result && (
        <div className="flex flex-col items-center p-6 rounded-lg bg-gray-50">
          <div className="flex gap-4 mb-6">
            <div className="w-24 h-24 rounded-lg overflow-hidden border-2 border-gray-200">
              <img src={documentImage} alt="Documento" className="w-full h-full object-cover" />
            </div>
            <div className="flex items-center">
              {result.matched ? (
                <CheckCircle className="w-8 h-8 text-green-500" />
              ) : result.requiresReview ? (
                <AlertTriangle className="w-8 h-8 text-yellow-500" />
              ) : (
                <XCircle className="w-8 h-8 text-red-500" />
              )}
            </div>
            <div className="w-24 h-24 rounded-lg overflow-hidden border-2 border-gray-200">
              {selfieImage && (
                <img src={selfieImage} alt="Selfie" className="w-full h-full object-cover" />
              )}
            </div>
          </div>

          <div className="text-center">
            <p className="text-2xl font-bold mb-2">
              {(result.similarity * 100).toFixed(1)}%
            </p>
            <p className="text-sm text-gray-500 mb-4">Similaridade</p>

            {result.matched && (
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">Faces correspondem!</span>
              </div>
            )}

            {result.requiresReview && (
              <div className="flex items-center gap-2 text-yellow-600">
                <AlertTriangle className="w-5 h-5" />
                <span className="font-medium">Requer revisão manual</span>
              </div>
            )}

            {!result.matched && !result.requiresReview && (
              <div className="flex flex-col items-center gap-2">
                <div className="flex items-center gap-2 text-red-600">
                  <XCircle className="w-5 h-5" />
                  <span className="font-medium">Faces não correspondem</span>
                </div>
                <button
                  onClick={handleRetry}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Tentar novamente
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
